import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { TrackedJobResult, TrackedJobStatus } from "src/generated/graphqlTypes";
import { usePageVisibility } from "src/hooks/usePageVisibility";
import { TrackedJobsPollQuery, useTrackedJobsPollQuery } from "src/hooks/useTrackedJobs.generated";

export type TrackedJobChildEntry = {
  jobId: string;
  status: TrackedJobStatus | undefined;
  progressPercent: number | undefined;
  progressMessage: string | undefined;
  downloadUrl: string | undefined;
  fileName: string | undefined;
};

export type TrackedJobEntry = TrackedJobChildEntry & { title: string; children?: TrackedJobChildEntry[] };

type TrackedJobInput = Pick<TrackedJobEntry, "jobId" | "title">;

type AddJobOptions = {
  onComplete?: (result: TrackedJobResult) => void;
  onFailed?: (message: string | undefined) => void;
  /** Poll without showing in the Downloads panel; auto-remove on terminal status. */
  silent?: boolean;
};

type TrackedJobsContextValue = {
  jobs: TrackedJobEntry[];
  /** Add trackedJob to the context by ID */
  addJob: (jobId: string, title: string, options?: AddJobOptions) => void;
  /** Add multiple tracked jobs to the context by ID and title */
  addJobs: (jobs: TrackedJobInput[]) => void;
  /* Add job parent to the context. This will polling for all tracked jobs with the same parent ID. */
  addJobParent: (parentId: string, title: string, onComplete?: () => void) => void;
  /** Dismiss a tracked job by ID */
  dismissTrackedJob: (jobId: string) => void;
  /** Dismiss a job parent by ID */
  dismissJobParent: (parentId: string) => void;
};

/** Exported for use in Storybook decorators */
export const TrackedJobsContext = createContext<TrackedJobsContextValue>({
  jobs: [],
  addJob: () => {},
  addJobParent: () => {},
  addJobs: () => {},
  dismissTrackedJob: () => {},
  dismissJobParent: () => {},
});
TrackedJobsContext.displayName = "TrackedJobsContext";

export function useTrackedJobs() {
  return useContext(TrackedJobsContext);
}

type TrackedIndividualJob = { title: string; jobId: string; silent?: boolean };
type TrackedJobParent = { title: string; parentId: string; jobId?: string };

export type TrackedJobMeta = TrackedIndividualJob & TrackedJobParent;

type PollJob = TrackedJobsPollQuery["trackedJobs"][number];

type JobHandlers = {
  onComplete?: (result?: TrackedJobResult) => void;
  onFailed?: (message: string | undefined) => void;
};

export function TrackedJobsProvider(props: PropsWithChildren<{ initialJobs?: TrackedJobMeta[] }>) {
  const { children, initialJobs = [] } = props;
  const [individualJobs, setIndividualJobs] = useState<TrackedIndividualJob[]>(initialJobs.filter((j) => j.jobId));
  const [jobParents, setJobParents] = useState<TrackedJobParent[]>(initialJobs.filter((j) => j.parentId));
  const handledRef = useRef<Set<string>>(new Set());
  const handlersRef = useRef<Map<string, JobHandlers>>(new Map());
  const isVisible = usePageVisibility();

  const hasJobs = individualJobs.nonEmpty || jobParents.nonEmpty;

  const { data, startPolling, stopPolling } = useTrackedJobsPollQuery({
    variables: {
      filter: {
        ...(individualJobs.nonEmpty && { id: individualJobs.map((j) => j.jobId) }),
        ...(jobParents.nonEmpty && { parent: jobParents.map((j) => j.parentId) }),
      },
    },
    skip: !hasJobs,
    fetchPolicy: "network-only",
  });

  const allTerminal =
    hasJobs &&
    data?.trackedJobs.every(
      (j) => j.status.code === TrackedJobStatus.Completed || j.status.code === TrackedJobStatus.Failed,
    );

  // Manage polling based on visibility and terminal status
  useEffect(() => {
    if (!hasJobs || allTerminal) {
      stopPolling();
      return;
    }
    if (isVisible) {
      const interval = data?.trackedJobs.map((j) => j.pollIntervalInMs).min() ?? 2000;
      startPolling(interval);
    } else {
      stopPolling();
    }
  }, [hasJobs, isVisible, allTerminal, data?.trackedJobs, startPolling, stopPolling]);

  const removeIndividualJob = useCallback((jobId: string) => {
    setIndividualJobs((prev) => prev.filter((m) => m.jobId !== jobId));
    handledRef.current.delete(jobId);
    handlersRef.current.delete(jobId);
  }, []);

  // Handle terminal jobs: auto-download Assets, call handlers, or clean up failed/silent jobs
  useEffect(() => {
    data?.trackedJobs.forEach((job) => {
      if (handledRef.current.has(job.id)) return;

      if (job.status.code === TrackedJobStatus.Completed) {
        handledRef.current.add(job.id);
        if (job.result?.__typename === "Asset" && job.result.attachmentUrl) {
          const link = document.createElement("a");
          link.href = job.result.attachmentUrl;
          link.download = job.result.fileName ?? "";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          handlersRef.current.get(job.id)?.onComplete?.(job.result as TrackedJobResult);
        }
        const silent = individualJobs.find((m) => m.jobId === job.id)?.silent;
        handlersRef.current.delete(job.id);
        if (silent) {
          setIndividualJobs((prev) => prev.filter((m) => m.jobId !== job.id));
          handledRef.current.delete(job.id);
        }
      } else if (job.status.code === TrackedJobStatus.Failed) {
        handledRef.current.add(job.id);
        handlersRef.current.get(job.id)?.onFailed?.(job.progressMessage ?? undefined);
        const silent = individualJobs.find((m) => m.jobId === job.id)?.silent;
        handlersRef.current.delete(job.id);
        if (silent) {
          setIndividualJobs((prev) => prev.filter((m) => m.jobId !== job.id));
          handledRef.current.delete(job.id);
        }
      }

      // Call onComplete for parents if all children have been processed
      jobParents
        .filter((parent) => handlersRef.current.has(parent.parentId))
        .forEach((parent) => {
          const jobs = data?.trackedJobs.filter((j) => j.parent?.id === parent.parentId) ?? [];
          const hasProcessed =
            jobs.nonEmpty &&
            jobs.every((j) => [TrackedJobStatus.Completed, TrackedJobStatus.Failed].includes(j.status.code));
          if (hasProcessed) handlersRef.current.get(parent.parentId)?.onComplete?.();
        });
    });
  }, [data?.trackedJobs, jobParents, individualJobs]);

  const addJob = useCallback((jobId: string, title: string, options?: AddJobOptions) => {
    setIndividualJobs((prev) => {
      if (prev.some((m) => m.jobId === jobId)) return prev;
      return [...prev, { jobId, title, silent: options?.silent }];
    });
    if (options?.onComplete || options?.onFailed) {
      handlersRef.current.set(jobId, {
        onComplete: options.onComplete ? (result) => options.onComplete?.(result as TrackedJobResult) : undefined,
        onFailed: options.onFailed,
      });
    }
  }, []);

  const addJobParent = useCallback((parentId: string, title: string, onComplete?: () => void) => {
    setJobParents((prev) => {
      if (prev.some((m) => m.parentId === parentId)) return prev;
      return [...prev, { parentId, title }];
    });
    if (onComplete) {
      handlersRef.current.set(parentId, {
        onComplete: () => {
          onComplete?.();
          dismissJobParent(parentId);
        },
      });
    }
  }, []);

  const addJobs = useCallback(
    (jobs: TrackedJobInput[]) => {
      jobs.forEach((job) => addJob(job.jobId, job.title));
    },
    [addJob],
  );

  const dismissTrackedJob = useCallback(
    (jobId: string) => {
      removeIndividualJob(jobId);
    },
    [removeIndividualJob],
  );

  const dismissJobParent = useCallback(
    (parentId: string) => {
      setJobParents((prev) => prev.filter((m) => m.parentId !== parentId));
      data?.trackedJobs.filter((j) => j.parent?.id === parentId).forEach((j) => handledRef.current.delete(j.id));
      handlersRef.current.delete(parentId);
    },
    [data?.trackedJobs],
  );

  const jobs = useMemo<TrackedJobEntry[]>(() => {
    // Silent jobs are polled but omitted from the Downloads panel.
    const directJobs: TrackedJobEntry[] = individualJobs
      .filter((meta) => !meta.silent)
      .map((meta) => {
        const job = data?.trackedJobs.find((j) => j.id === meta.jobId);
        return toJobEntry(meta.jobId, meta.title, job);
      });

    const jobParentJobs: TrackedJobEntry[] = jobParents.map((meta) => {
      // Exclude COMPLETED children so old jobs from prior operations don't appear
      const childJobs =
        data?.trackedJobs.filter(
          (j) => j.parent?.id === meta.parentId && j.status.code !== TrackedJobStatus.Completed,
        ) ?? [];
      const childEntries: TrackedJobChildEntry[] = childJobs.map((job) => toChildEntry(job));

      const allFailed = childEntries.nonEmpty && childEntries.every((c) => c.status === TrackedJobStatus.Failed);
      // Once poll data arrives and no non-completed children remain, all jobs have completed
      const allCompleted = !!data && childEntries.isEmpty;
      const status = allCompleted
        ? TrackedJobStatus.Completed
        : allFailed
          ? TrackedJobStatus.Failed
          : childEntries.nonEmpty
            ? TrackedJobStatus.Running
            : undefined;

      return {
        jobId: meta.parentId,
        title: meta.title,
        status,
        progressPercent: undefined,
        progressMessage: undefined,
        downloadUrl: undefined,
        fileName: undefined,
        children: childEntries,
      };
    });

    return [...directJobs, ...jobParentJobs];
  }, [data, individualJobs, jobParents]);

  return (
    <TrackedJobsContext.Provider value={{ jobs, addJob, addJobParent, addJobs, dismissTrackedJob, dismissJobParent }}>
      {children}
    </TrackedJobsContext.Provider>
  );
}

function toJobEntry(jobId: string, title: string, job: PollJob | undefined): TrackedJobEntry {
  const progressPercent =
    job?.progressCurrent != null && job?.progressTotal
      ? Math.round((job.progressCurrent / job.progressTotal) * 100)
      : undefined;
  return {
    jobId,
    title,
    status: job?.status.code,
    progressPercent,
    progressMessage: job?.progressMessage ?? undefined,
    downloadUrl: job?.result?.__typename === "Asset" ? job.result.attachmentUrl : undefined,
    fileName: job?.result?.__typename === "Asset" ? (job.result.fileName ?? undefined) : undefined,
  };
}

function toChildEntry(job: PollJob): TrackedJobChildEntry {
  const progressPercent =
    job.progressCurrent != null && job.progressTotal
      ? Math.round((job.progressCurrent / job.progressTotal) * 100)
      : undefined;
  return {
    jobId: job.id,
    status: job.status.code,
    progressPercent,
    progressMessage: job.progressMessage ?? undefined,
    downloadUrl: job.result?.__typename === "Asset" ? job.result.attachmentUrl : undefined,
    fileName: job.result?.__typename === "Asset" ? (job.result.fileName ?? undefined) : undefined,
  };
}
